/**
 * @nanokart/tryon-studio-sdk
 * useInventory hook - Manage product inventory
 */

import { useCallback, useEffect } from 'react';
import { useStore } from './useStore';
import type { CatalogItem, CatalogFilters } from '../../types/config';

export interface UseInventoryReturn {
  /** List of inventory items */
  items: CatalogItem[];
  /** Currently selected item */
  selectedItem: CatalogItem | null;
  /** Active filters */
  filters: CatalogFilters;
  /** Whether loading */
  isLoading: boolean;
  /** Whether there are more items to load */
  hasMore: boolean;
  /** Current page */
  page: number;
  /** Error message */
  error: string | null;
  /** Select an item */
  selectItem: (item: CatalogItem | null) => void;
  /** Add a live camera-captured dress as a temporary catalog item */
  addCameraGarment: (file: File) => Promise<CatalogItem>;
  /** Update filters and reload */
  updateFilters: (filters: Partial<CatalogFilters>) => void;
  /** Load more items */
  loadMore: () => Promise<void>;
  /** Reload inventory */
  reload: () => Promise<void>;
  /** Get item by ID */
  getItemById: (productId: string) => CatalogItem | undefined;
}

/**
 * Hook for managing product inventory
 * 
 * @example
 * ```tsx
 * const { items, selectedItem, selectItem, updateFilters } = useInventory();
 * 
 * return (
 *   <div>
 *     <FilterPanel onFilterChange={updateFilters} />
 *     {items.map(item => (
 *       <ProductCard 
 *         key={item.productId} 
 *         item={item}
 *         selected={selectedItem?.productId === item.productId}
 *         onSelect={() => selectItem(item)}
 *       />
 *     ))}
 *   </div>
 * );
 * ```
 */
export function useInventory(): UseInventoryReturn {
  const items = useStore((s) => s.inventory.items);
  const selectedItem = useStore((s) => s.inventory.selectedItem);
  const filters = useStore((s) => s.inventory.filters);
  const isLoading = useStore((s) => s.inventory.isLoading);
  const pagination = useStore((s) => s.inventory.pagination);
  const error = useStore((s) => s.inventory.error);
  
  const selectItemAction = useStore((s) => s.selectItem);
  const addCameraGarmentAction = useStore((s) => s.addCameraGarment);
  const updateFiltersAction = useStore((s) => s.updateFilters);
  const loadInventoryAction = useStore((s) => s.loadInventory);
  const loadMoreAction = useStore((s) => s.loadMore);

  const selectItem = useCallback(
    (item: CatalogItem | null) => {
      selectItemAction(item);
    },
    [selectItemAction]
  );

  const addCameraGarment = useCallback(
    (file: File) => addCameraGarmentAction(file),
    [addCameraGarmentAction]
  );

  const updateFilters = useCallback(
    (newFilters: Partial<CatalogFilters>) => {
      updateFiltersAction(newFilters);
      // Reload with new filters
      loadInventoryAction(newFilters, false);
    },
    [updateFiltersAction, loadInventoryAction]
  );

  const loadMore = useCallback(async () => {
    if (!isLoading && pagination.hasMore) {
      await loadMoreAction();
    }
  }, [isLoading, pagination.hasMore, loadMoreAction]);

  const reload = useCallback(async () => {
    await loadInventoryAction(filters, false);
  }, [loadInventoryAction, filters]);

  const getItemById = useCallback(
    (productId: string) => {
      return items.find((item) => item.productId === productId);
    },
    [items]
  );

  return {
    items,
    selectedItem,
    filters,
    isLoading,
    hasMore: pagination.hasMore,
    page: pagination.page,
    error,
    selectItem,
    addCameraGarment,
    updateFilters,
    loadMore,
    reload,
    getItemById,
  };
}
